import { useCallback } from "react"
import {
  Assessment_Takes_Bool_Exp,
  Enum_Question_Type_Enum,
  GetAssessmentPagesInAssessmentDocument,
  GetAssessmentPagesInAssessmentQuery,
  GetAssessmentPagesInAssessmentQueryVariables,
  GetAssessmentQuestionsByPageIdDocument,
  GetAssessmentQuestionsByPageIdQuery,
  GetAssessmentQuestionsByPageIdQueryVariables,
  GetAssessmentQuestionsWithIdsInDocument,
  GetAssessmentQuestionsWithIdsInQuery,
  GetAssessmentQuestionsWithIdsInQueryVariables,
  GetAssessmentResultsDocument,
  GetAssessmentResultsQuery,
  GetAssessmentResultsQueryVariables,
  GetAssessmentsWithFilterDocument,
  GetAssessmentsWithFilterQuery,
  GetAssessmentsWithFilterQueryVariables,
  GetPopulatedAssessmentDocument,
  GetPopulatedAssessmentQuery,
  GetPopulatedAssessmentQueryVariables,
  Timestamptz_Comparison_Exp,
} from "../generated/graphql"
import {
  Assessment,
  AssessmentTake,
  PageProps,
  PopulatedAssessment,
  QuestionProps,
  QuestionResult,
  SelectQuestionResult,
  SwitchBoardQuestionResult,
} from "../utils/types"
import { useGraphQLClient } from "./useGraphQLClient"

const ACTIVE_ASSESSMENT_FILTER: GetAssessmentsWithFilterQueryVariables["where"] =
  {
    deleted_at: { _is_null: true },
  }

const DEFAULT_RESULTS_LIMIT = 50
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type QuestionProperties = {
  options?: Array<{
    index?: number
    label?: string
    title?: string
    subtitle?: string
  }>
} & Record<string, unknown>

const isUuid = (value?: string) => UUID_REGEX.test(value ?? "")

const safeJsonParse = <T,>(value?: string | null): T | undefined => {
  if (!value) {
    return undefined
  }

  try {
    return JSON.parse(value) as T
  } catch {
    return undefined
  }
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

const parseTimeAnswer = (answer: string): number | undefined => {
  const numeric = toNumber(answer)
  if (typeof numeric === "number") {
    return numeric
  }

  const normalized = answer.trim().toLowerCase()
  const meridiemMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/)
  if (meridiemMatch) {
    const hours = toNumber(meridiemMatch[1])
    const minutes = toNumber(meridiemMatch[2])

    if (typeof hours === "number" && typeof minutes === "number") {
      let hours24 = hours % 12
      if (meridiemMatch[3] === "pm") {
        hours24 += 12
      }
      return hours24 + minutes / 60
    }
  }

  if (normalized.includes(":")) {
    const [hoursStr, minutesStr] = normalized.split(":")
    const hours = toNumber(hoursStr)
    const minutes = toNumber(minutesStr)

    if (typeof hours === "number" && typeof minutes === "number") {
      return hours + minutes / 60
    }
  }

  return undefined
}

const parseBooleanAnswer = (answer: string): boolean | undefined => {
  const normalized = answer.toLowerCase().trim()

  if (["true", "1", "yes"].includes(normalized)) {
    return true
  }

  if (["false", "0", "no"].includes(normalized)) {
    return false
  }

  return undefined
}

const getOptionLabel = (
  properties: QuestionProperties | undefined,
  index: number | undefined
): string | undefined => {
  if (typeof index !== "number" || !properties?.options) {
    return undefined
  }

  const option = properties.options.find(
    (opt) => typeof opt.index === "number" && opt.index === index
  )

  return option?.label ?? option?.title ?? undefined
}

const parseSelectAnswer = (
  answer: string,
  properties?: QuestionProperties
): SelectQuestionResult | undefined => {
  const parsed = safeJsonParse<{
    index?: number | string
    label?: string
    value?: number | string
  }>(answer)

  if (parsed) {
    const index = toNumber(parsed.index) ?? toNumber(parsed.value)

    if (typeof index === "number") {
      return {
        index,
        label: parsed.label ?? getOptionLabel(properties, index) ?? "",
      }
    }
  }

  const numericAnswer = toNumber(answer)
  if (typeof numericAnswer === "number") {
    return {
      index: numericAnswer,
      label: getOptionLabel(properties, numericAnswer) ?? `${numericAnswer}`,
    }
  }

  if (answer) {
    return {
      index: -1,
      label: answer,
    }
  }

  return undefined
}

const parseSwitchboardAnswer = (
  answer: string
): SwitchBoardQuestionResult[] | undefined => {
  const parsed = safeJsonParse<unknown>(answer)

  const buildResult = (index: number | undefined, value: boolean | undefined) =>
    typeof index === "number" && typeof value === "boolean"
      ? { index, value }
      : undefined

  if (Array.isArray(parsed)) {
    const mapped = parsed
      .map((entry) => {
        if (typeof entry === "number" || typeof entry === "string") {
          return buildResult(toNumber(entry), true)
        }

        if (entry && typeof entry === "object") {
          const candidate = entry as {
            index?: number | string
            value?: boolean
            selected?: boolean
          }

          const index = toNumber(candidate.index)
          const value =
            typeof candidate.value === "boolean"
              ? candidate.value
              : candidate.selected

          return buildResult(
            index,
            typeof value === "boolean" ? value : undefined
          )
        }

        return undefined
      })
      .filter((item): item is SwitchBoardQuestionResult => Boolean(item))

    return mapped.length ? mapped : undefined
  }

  if (parsed && typeof parsed === "object") {
    const entries = Object.entries(parsed).map(([key, value]) =>
      buildResult(toNumber(key), typeof value === "boolean" ? value : undefined)
    )
    const filtered = entries.filter((item): item is SwitchBoardQuestionResult =>
      Boolean(item)
    )
    return filtered.length ? filtered : undefined
  }

  const segments = answer
    .split(/[,\s]+/)
    .map((segment) => toNumber(segment))
    .filter((value): value is number => typeof value === "number")

  if (segments.length) {
    const fallbackResults = segments
      .map((index) => buildResult(index, true))
      .filter((item): item is SwitchBoardQuestionResult => Boolean(item))

    if (fallbackResults.length) {
      return fallbackResults
    }
  }

  return undefined
}

const parseAnswerValue = (
  answer: string,
  questionType?: Enum_Question_Type_Enum | null,
  properties?: QuestionProperties
): QuestionResult["result"] => {
  if (!questionType) {
    return answer || undefined
  }

  switch (questionType) {
    case Enum_Question_Type_Enum.Boolean:
      return parseBooleanAnswer(answer)
    case Enum_Question_Type_Enum.Number:
    case Enum_Question_Type_Enum.Scale:
      return toNumber(answer)
    case Enum_Question_Type_Enum.Time:
      return parseTimeAnswer(answer)
    case Enum_Question_Type_Enum.Select:
      return parseSelectAnswer(answer, properties)
    case Enum_Question_Type_Enum.Switchboard:
      return parseSwitchboardAnswer(answer)
    case Enum_Question_Type_Enum.Note:
    default:
      return answer || undefined
  }
}

const buildCompletedAtFilter = (
  before?: string,
  after?: string
): Timestamptz_Comparison_Exp | undefined => {
  if (!before && !after) {
    return undefined
  }

  const comparison: Timestamptz_Comparison_Exp = {}

  if (before) {
    comparison._lte = before
  }

  if (after) {
    comparison._gte = after
  }

  return comparison
}

const buildAssessmentResult = (
  take: GetAssessmentResultsQuery["assessment_takes"][number]
): AssessmentTake => ({
  assessmentId: take.assessment_id,
  takeId: take.id,
  startedAt: take.started_at,
  completedAt: take.completed_at,
  results: take.answers.map((answer) => ({
    questionId: answer.question_id,
    customPromptId: answer.custom_prompt_id ?? null,
    result: parseAnswerValue(
      answer.answer,
      answer.question?.question_type,
      answer.question?.properties as QuestionProperties | undefined
    ),
  })),
})

const mapAssessment = (
  assessment: GetAssessmentsWithFilterQuery["assessments"][number]
): Assessment => ({
  id: assessment.id,
  title: assessment.title,
  subtitle: assessment.subtitle ?? "",
  version: assessment.schema_version,
  firstPageId: assessment.first_page_id,
  assessmentLength: assessment.assessment_length,
  createdAt: assessment.created_at,
  updatedAt: assessment.updated_at,
  deletedAt: assessment.deleted_at ?? undefined,
})

const mapPopulatedAssessment = (
  assessment: GetPopulatedAssessmentQuery["assessments_by_pk"]
): PopulatedAssessment | null => {
  if (!assessment) {
    return null
  }
  return {
    ...mapAssessment(assessment),
    pages: assessment.assessments_assessment_pages.map((page) => ({
      ...mapPage(page),
      questions: page.questions
        .sort((a, b) => a.display_order - b.display_order)
        .map((question) => mapQuestion(question)),
    })),
  }
}

const mapPage = (
  page: GetAssessmentPagesInAssessmentQuery["assessment_pages"][number]
): PageProps => ({
  id: page.id,
  assessmentId: page.assessment_id,
  displayOrder: page.display_order,
  showCautionModal: page.show_caution_modal,
  createdAt: page.created_at,
  updatedAt: page.updated_at,
})

const mapQuestion = (
  question: GetAssessmentQuestionsByPageIdQuery["assessment_questions"][number]
): QuestionProps => ({
  id: question.id,
  pageId: question.page_id,
  questionType: question.question_type,
  displayOrder: question.display_order,
  text: question.text,
  explanationText: question.explanation_text ?? undefined,
  properties: question.properties ?? undefined,
  createdAt: question.created_at,
  updatedAt: question.updated_at,
})

/**
 * Provides memoized GraphQL-powered helpers for retrieving assessments, pages, questions,
 * and client-specific assessment results. Each helper validates UUID inputs before issuing
 * the query and returns normalized data through the associated mapping utilities.
 * @returns Accessors for assessments, populated assessments, pages, questions,
 * and assessment results scoped to a GraphQL client instance.
 */
const useAssessment = () => {
  const graphQLClient = useGraphQLClient()

  const getAssessments = useCallback(
    async (where?: GetAssessmentsWithFilterQueryVariables["where"]) => {
      const response = await graphQLClient.request<
        GetAssessmentsWithFilterQuery,
        GetAssessmentsWithFilterQueryVariables
      >(GetAssessmentsWithFilterDocument, {
        where: where ?? ACTIVE_ASSESSMENT_FILTER,
      })

      return response.assessments.map(mapAssessment)
    },
    [graphQLClient]
  )

  // Get assessment with uuid
  const getPopulatedAssessment = useCallback(
    async (assessmentId: string): Promise<PopulatedAssessment | null> => {
      if (!isUuid(assessmentId)) {
        return null
      }
      const response = await graphQLClient.request<
        GetPopulatedAssessmentQuery,
        GetPopulatedAssessmentQueryVariables
      >(GetPopulatedAssessmentDocument, { assessmentId })

      return response.assessments_by_pk
        ? mapPopulatedAssessment(response.assessments_by_pk)
        : null
    },
    [graphQLClient]
  )
  const getPages = useCallback(
    async (assessmentId: string) => {
      if (!isUuid(assessmentId)) {
        return []
      }

      const response = await graphQLClient.request<
        GetAssessmentPagesInAssessmentQuery,
        GetAssessmentPagesInAssessmentQueryVariables
      >(GetAssessmentPagesInAssessmentDocument, { assessmentId })

      return response.assessment_pages.map(mapPage)
    },
    [graphQLClient]
  )

  const getQuestions = useCallback(
    async (pageId: string) => {
      if (!isUuid(pageId)) {
        return []
      }

      const response = await graphQLClient.request<
        GetAssessmentQuestionsByPageIdQuery,
        GetAssessmentQuestionsByPageIdQueryVariables
      >(GetAssessmentQuestionsByPageIdDocument, { pageId })

      return response.assessment_questions.map(mapQuestion)
    },
    [graphQLClient]
  )

  const getAssessmentResults = useCallback(
    async (
      assessmentId: string,
      clientId: string,
      start?: string,
      end?: string
    ) => {
      if (!isUuid(assessmentId) || !isUuid(clientId)) {
        return []
      }

      const where: Assessment_Takes_Bool_Exp = {
        assessment_id: { _eq: assessmentId },
        user_id: { _eq: clientId },
      }

      const completedAt = buildCompletedAtFilter(end, start)
      if (completedAt) {
        where.completed_at = completedAt
      }

      const response = await graphQLClient.request<
        GetAssessmentResultsQuery,
        GetAssessmentResultsQueryVariables
      >(GetAssessmentResultsDocument, {
        where,
        limit: DEFAULT_RESULTS_LIMIT,
      })
      return response.assessment_takes.map(buildAssessmentResult)
    },
    [graphQLClient]
  )

  const getAssessmentQuestionsWithIdsIn = useCallback(
    async (ids: string[]) => {
      if (ids.some((id) => !isUuid(id))) {
        return []
      }

      const response = await graphQLClient.request<
        GetAssessmentQuestionsWithIdsInQuery,
        GetAssessmentQuestionsWithIdsInQueryVariables
      >(GetAssessmentQuestionsWithIdsInDocument, { _in: ids })

      return response.assessment_questions.map(mapQuestion)
    },
    [graphQLClient]
  )

  return {
    getAssessments,
    getPages,
    getQuestions,
    getAssessmentResults,
    getPopulatedAssessment,
    getAssessmentQuestionsWithIdsIn,
  }
}

export default useAssessment
